import { useMemo, useState } from 'react';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { ConfirmationDialogV2, ErpDataTableV2, ErpInputTableV2 } from '@/components/custom/erp';
import { getDataTableColumns, getInputTableColumns } from './cols-definition';
import { ActionBar, FormDialog, SearchDialog } from './components';
import { useCRUD, useForms, useRows } from '@/hooks';
import { QUERY_KEYS } from '@/constants';

export default function ChungTuBuTruCongNoPage() {
  const [showSearchDialog, setShowSearchDialog] = useState(false);
  const {
    data: chungTuBuTruCongNos,
    isLoading,
    totalItems,
    currentPage,
    handlePageChange,
    detail,
    fetchDetail,
    addItem: addChungTuBuTruCongNo,
    updateItem: updateChungTuBuTruCongNo,
    deleteItem: deleteChungTuBuTruCongNo,
    refreshData: refreshChungTuBuTruCongNos
  } = useCRUD({
    endpoint: QUERY_KEYS.CHUNG_TU_PHAI_THU_KHAC_CHUNG_TU_BU_TRU_CONG_NO
  });
  const { selectedObj, selectedRowIndex, handleRowClick, clearSelection } = useRows();
  const {
    showForm,
    showDelete,
    formMode,
    isCopyMode,

    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick
  } = useForms(fetchDetail);

  const handleSearch = () => {
    setShowSearchDialog(true);
  };

  const handleSearchClose = () => {
    setShowSearchDialog(false);
  };

  const handleSearchSubmit = (filters: BanHangPayload) => {};

  const handleSubmit = async (data: BanHangPayload) => {
    try {
      if (formMode === 'add') {
        await addChungTuBuTruCongNo(data);
      } else if (formMode === 'edit' && selectedObj) {
        await updateChungTuBuTruCongNo(selectedObj.uuid, data);
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const mainColumns = useMemo(() => getDataTableColumns(handleViewClick), [handleViewClick]);
  const previewColumns = useMemo(() => getInputTableColumns(), []);

  const tables = [
    {
      name: 'Tất cả',
      rows: chungTuBuTruCongNos,
      columns: mainColumns
    },
    {
      name: 'Chưa ghi sổ',
      rows: chungTuBuTruCongNos.filter(row => row.status === '0'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#EAD1DC]' />
    },
    {
      name: 'Chờ duyệt',
      rows: chungTuBuTruCongNos.filter(row => row.status === '3'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-red-500' />
    },
    {
      name: 'Đã ghi sổ',
      rows: chungTuBuTruCongNos.filter(row => row.status === '5'),
      columns: mainColumns,
      icon: <div className='mr-2 size-[7px] rounded-full bg-[#3D85C6]' />
    }
  ];

  return (
    <div className='flex h-full min-h-[calc(100vh-120px)] w-full flex-col overflow-hidden'>
      {showSearchDialog && (
        <SearchDialog open={showSearchDialog} onClose={handleSearchClose} onSearch={handleSearchSubmit} />
      )}

      {showForm && (
        <FormDialog
          formMode={formMode}
          open={showForm}
          onClose={handleCloseForm}
          initialData={formMode === 'add' && !isCopyMode ? undefined : detail}
          onSubmit={handleSubmit}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
        />
      )}

      {showDelete && (
        <ConfirmationDialogV2
          open={showDelete}
          onClose={handleCloseDelete}
          onConfirm={() => {
            if (selectedObj?.uuid) deleteChungTuBuTruCongNo(selectedObj.uuid);
            clearSelection();
            handleCloseDelete();
          }}
        />
      )}

      {!showForm && (
        <>
          <ActionBar
            onAdd={handleAddClick}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onCopy={handleCopyClick}
            onSearch={handleSearch}
            onRefresh={async () => {
              await refreshChungTuBuTruCongNos();
              if (selectedObj) await fetchDetail(selectedObj.uuid);
            }}
            isEditDisabled={!selectedObj}
          />

          <ResizablePanelGroup direction='vertical' className='flex-1 overflow-hidden'>
            <ResizablePanel defaultSize={50} minSize={20}>
              <div className='h-full w-full overflow-hidden' data-perf-id='ct-phai-thu-khac-bu-tru-master-table'>
                <ErpDataTableV2
                  tables={tables}
                  onRowClick={handleRowClick}
                  selectedRowId={selectedRowIndex || undefined}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                  loading={isLoading}
                />
              </div>
            </ResizablePanel>

            <ResizableHandle withHandle />

            <ResizablePanel defaultSize={50} minSize={20}>
              <div className='h-full overflow-hidden' data-perf-id='ct-phai-thu-khac-bu-tru-bottom-detail-table'>
                <ErpInputTableV2
                  rows={selectedObj && !isLoading ? detail?.chi_tiet_data || [] : []}
                  columns={previewColumns}
                  showIndexColumn
                  actionsDisabled={formMode === 'view' || !selectedObj}
                  height='100%'
                  loading={isLoading && !selectedObj}
                />
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </>
      )}
    </div>
  );
}
